import React, { useState } from 'react';
import { useSettings } from '../SettingsContext';
import { useNotifications } from '../NotificationContext';
import { trackCustomEvent, trackPageView } from '../../services/analyticsService';
import { AnalyticsConfig } from '../../types';

interface AnalyticsSettingsProps {
  onSave: () => void;
}

const AnalyticsSettings: React.FC<AnalyticsSettingsProps> = ({ onSave }) => {
  const { settings, updateSettings } = useSettings();
  const { addNotification } = useNotifications();

  const [config, setConfig] = useState<AnalyticsConfig>(() => ({
    googleAnalyticsId: settings.analyticsConfig?.googleAnalyticsId || '',
    isGoogleAnalyticsEnabled: settings.analyticsConfig?.isGoogleAnalyticsEnabled ?? false,
    facebookPixelId: settings.analyticsConfig?.facebookPixelId || '',
    isFacebookPixelEnabled: settings.analyticsConfig?.isFacebookPixelEnabled ?? false,
    clarityProjectId: settings.analyticsConfig?.clarityProjectId || '',
    isClarityEnabled: settings.analyticsConfig?.isClarityEnabled ?? false,
    trackPageViews: settings.analyticsConfig?.trackPageViews ?? true,
    trackPurchases: settings.analyticsConfig?.trackPurchases ?? true,
    trackCheckouts: settings.analyticsConfig?.trackCheckouts ?? true,
  }));

  const [testingService, setTestingService] = useState<string | null>(null);

  const handleSave = (e: React.FormEvent) => {
    e.preventDefault();
    updateSettings({ analyticsConfig: config });
    onSave();
    addNotification({
      type: 'success',
      title: 'Tracking Saved',
      message: 'Analytics configuration updated and tracking tags synchronized.',
    });
  };

  const handleTestEvent = (service: 'ga' | 'fb' | 'clarity') => {
    setTestingService(service);
    try {
      trackCustomEvent(`test_${service}_event`, {
        timestamp: new Date().toISOString(),
        triggered_from: 'platform_settings',
      });
      trackPageView('Settings - Analytics Verification');
      addNotification({
        type: 'success',
        title: 'Test Signal Sent',
        message: `Dispatched test activity to ${service.toUpperCase()} tracking engine.`,
      });
    } catch (err) {
      addNotification({
        type: 'error',
        title: 'Signal Failed',
        message: 'Could not deliver test signal. Check tag IDs.',
      });
    } finally {
      setTimeout(() => setTestingService(null), 1200);
    }
  };

  return (
    <form onSubmit={handleSave} className="space-y-6 animate-in slide-in-from-left-4">
      <div className="bg-white dark:bg-slate-800 rounded-3xl border border-slate-200 dark:border-slate-700 shadow-sm p-6 space-y-6">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between pb-4 border-b border-slate-100 dark:border-slate-700 gap-2">
          <div>
            <h2 className="text-xl font-black text-slate-900 dark:text-white tracking-tight">Analytics & Attribution</h2>
            <p className="text-xs text-slate-500 font-medium">Connect external measurement platforms to monitor conversions and visitor behavior.</p>
          </div>
          <button
            type="submit"
            className="px-5 py-2.5 bg-indigo-600 hover:bg-indigo-700 text-white text-xs font-bold uppercase tracking-wider rounded-xl transition-all shadow-sm"
          >
            Save Changes
          </button>
        </div>

        {/* Google Analytics 4 */}
        <div className="p-5 rounded-2xl bg-slate-50 dark:bg-slate-900/50 border border-slate-200/80 dark:border-slate-700 space-y-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-xl bg-amber-500/10 text-amber-600 dark:text-amber-400 flex items-center justify-center font-black">
                <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M3 3v18h18"/><path d="m19 9-5 5-4-4-3 3"/></svg>
              </div>
              <div>
                <h3 className="text-sm font-black text-slate-900 dark:text-white">Google Analytics 4 (GA4)</h3>
                <p className="text-[11px] text-slate-500">Track page views, checkout flow drop-offs, and completed purchases.</p>
              </div>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={config.isGoogleAnalyticsEnabled}
                onChange={(e) => setConfig({ ...config, isGoogleAnalyticsEnabled: e.target.checked })}
                className="sr-only peer"
              />
              <div className="w-10 h-6 bg-slate-200 peer-focus:outline-none rounded-full peer dark:bg-slate-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-slate-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-indigo-600"></div>
            </label>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-4 gap-3">
            <div className="sm:col-span-3">
              <input
                type="text"
                value={config.googleAnalyticsId}
                onChange={(e) => setConfig({ ...config, googleAnalyticsId: e.target.value })}
                placeholder="G-XXXXXXXXXX"
                className="w-full px-3.5 py-2 text-xs font-mono rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-900 dark:text-white focus:ring-2 focus:ring-indigo-500 focus:outline-none"
              />
            </div>
            <button
              type="button"
              disabled={!config.isGoogleAnalyticsEnabled || !config.googleAnalyticsId || testingService === 'ga'}
              onClick={() => handleTestEvent('ga')}
              className="px-3 py-2 text-xs font-bold text-slate-700 dark:text-slate-200 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors disabled:opacity-50"
            >
              {testingService === 'ga' ? 'Testing...' : 'Send Test Event'}
            </button>
          </div>
        </div>

        {/* Facebook Pixel */}
        <div className="p-5 rounded-2xl bg-slate-50 dark:bg-slate-900/50 border border-slate-200/80 dark:border-slate-700 space-y-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-xl bg-blue-500/10 text-blue-600 dark:text-blue-400 flex items-center justify-center font-black">
                <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M18 2h-3a5 5 0 0 0-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 0 1 1-1h3z"/></svg>
              </div>
              <div>
                <h3 className="text-sm font-black text-slate-900 dark:text-white">Facebook (Meta) Pixel</h3>
                <p className="text-[11px] text-slate-500">Measure ad campaign ROAS and trigger checkout retargeting signals.</p>
              </div>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={config.isFacebookPixelEnabled}
                onChange={(e) => setConfig({ ...config, isFacebookPixelEnabled: e.target.checked })}
                className="sr-only peer"
              />
              <div className="w-10 h-6 bg-slate-200 peer-focus:outline-none rounded-full peer dark:bg-slate-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-slate-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-indigo-600"></div>
            </label>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-4 gap-3">
            <div className="sm:col-span-3">
              <input
                type="text"
                value={config.facebookPixelId}
                onChange={(e) => setConfig({ ...config, facebookPixelId: e.target.value })}
                placeholder="123456789012345"
                className="w-full px-3.5 py-2 text-xs font-mono rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-900 dark:text-white focus:ring-2 focus:ring-indigo-500 focus:outline-none"
              />
            </div>
            <button
              type="button"
              disabled={!config.isFacebookPixelEnabled || !config.facebookPixelId || testingService === 'fb'}
              onClick={() => handleTestEvent('fb')}
              className="px-3 py-2 text-xs font-bold text-slate-700 dark:text-slate-200 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors disabled:opacity-50"
            >
              {testingService === 'fb' ? 'Testing...' : 'Send Test Event'}
            </button>
          </div>
        </div>

        {/* Microsoft Clarity */}
        <div className="p-5 rounded-2xl bg-slate-50 dark:bg-slate-900/50 border border-slate-200/80 dark:border-slate-700 space-y-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-xl bg-cyan-500/10 text-cyan-600 dark:text-cyan-400 flex items-center justify-center font-black">
                <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><path d="m10 15 5-3-5-3v6Z"/></svg>
              </div>
              <div>
                <h3 className="text-sm font-black text-slate-900 dark:text-white">Microsoft Clarity</h3>
                <p className="text-[11px] text-slate-500">Record customer sessions and visualize friction with interaction heatmaps.</p>
              </div>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={config.isClarityEnabled}
                onChange={(e) => setConfig({ ...config, isClarityEnabled: e.target.checked })}
                className="sr-only peer"
              />
              <div className="w-10 h-6 bg-slate-200 peer-focus:outline-none rounded-full peer dark:bg-slate-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-slate-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-indigo-600"></div>
            </label>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-4 gap-3">
            <div className="sm:col-span-3">
              <input
                type="text"
                value={config.clarityProjectId}
                onChange={(e) => setConfig({ ...config, clarityProjectId: e.target.value })}
                placeholder="abcdefghij"
                className="w-full px-3.5 py-2 text-xs font-mono rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-900 dark:text-white focus:ring-2 focus:ring-indigo-500 focus:outline-none"
              />
            </div>
            <button
              type="button"
              disabled={!config.isClarityEnabled || !config.clarityProjectId || testingService === 'clarity'}
              onClick={() => handleTestEvent('clarity')}
              className="px-3 py-2 text-xs font-bold text-slate-700 dark:text-slate-200 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors disabled:opacity-50"
            >
              {testingService === 'clarity' ? 'Testing...' : 'Send Test Event'}
            </button>
          </div>
        </div>

        {/* Granular Tracking Toggles */}
        <div className="pt-2 border-t border-slate-100 dark:border-slate-700">
          <p className="text-[11px] font-black text-slate-400 uppercase tracking-wider mb-3">Automatic Event Triggers</p>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <label className="flex items-center gap-2 text-xs font-bold text-slate-700 dark:text-slate-300 cursor-pointer">
              <input
                type="checkbox"
                checked={config.trackPageViews}
                onChange={(e) => setConfig({ ...config, trackPageViews: e.target.checked })}
                className="rounded border-slate-300 text-indigo-600 focus:ring-indigo-500"
              />
              Track Page Views
            </label>
            <label className="flex items-center gap-2 text-xs font-bold text-slate-700 dark:text-slate-300 cursor-pointer">
              <input
                type="checkbox"
                checked={config.trackCheckouts}
                onChange={(e) => setConfig({ ...config, trackCheckouts: e.target.checked })}
                className="rounded border-slate-300 text-indigo-600 focus:ring-indigo-500"
              />
              Track Checkout Starts
            </label>
            <label className="flex items-center gap-2 text-xs font-bold text-slate-700 dark:text-slate-300 cursor-pointer">
              <input
                type="checkbox"
                checked={config.trackPurchases}
                onChange={(e) => setConfig({ ...config, trackPurchases: e.target.checked })}
                className="rounded border-slate-300 text-indigo-600 focus:ring-indigo-500"
              />
              Track Successful Orders
            </label>
          </div>
        </div>
      </div>
    </form>
  );
};

export default AnalyticsSettings;
